import { useCallback, useEffect, useState } from 'react';
import api, { errMsg } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import WeekBoard from '../components/ludo/sched/WeekBoard';
import StaffBoard from '../components/ludo/sched/StaffBoard';
import GapsPanel from '../components/ludo/sched/GapsPanel';
import StaffManager from '../components/ludo/sched/StaffManager';
import SessionDialog, { Lbl } from '../components/ludo/sched/SessionDialog';
import CoverDialog from '../components/ludo/sched/CoverDialog';

const TAB = 'rounded-full border border-[#E5E0EA] data-[state=active]:bg-[#5E5068] data-[state=active]:text-white text-[12px]';
const iso = (d) => d.toISOString().slice(0, 10);
const shift = (s, days) => { const d = new Date(`${s}T12:00:00`); d.setDate(d.getDate() + days); return iso(d); };

const Stat = ({ label, value, tone, testid }) => (
  <div className="rounded-2xl border border-[#E5E0EA] bg-white px-4 py-3" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wider text-[#8A7B95] font-semibold">{label}</div>
    <div className={`text-2xl font-serif ${tone || 'text-[#2D2833]'}`}>{value}</div>
  </div>
);

const SettingsForm = ({ settings, meta, onSaved }) => {
  const [f, setF] = useState(settings);
  const save = async () => { try { await api.put('/ludo/schedule/settings', { ...f, supervision_pct: Number(f.supervision_pct) }); toast.success('Settings saved'); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="grid sm:grid-cols-4 gap-3 items-end max-w-3xl" data-testid="sched-settings">
      <Lbl t="Supervision % of direct hrs"><Input type="number" value={f.supervision_pct} onChange={(e) => setF({ ...f, supervision_pct: e.target.value })} className="h-9 rounded-xl" data-testid="sched-settings-sup" /></Lbl>
      <Lbl t="Center opens"><Input type="time" value={f.open_time} onChange={(e) => setF({ ...f, open_time: e.target.value })} className="h-9 rounded-xl" data-testid="sched-settings-open" /></Lbl>
      <Lbl t="Center closes"><Input type="time" value={f.close_time} onChange={(e) => setF({ ...f, close_time: e.target.value })} className="h-9 rounded-xl" data-testid="sched-settings-close" /></Lbl>
      <Button onClick={save} className="rounded-xl bg-[#5E5068] text-white h-9" data-testid="sched-settings-save">Save</Button>
      <div className="sm:col-span-4 flex gap-1 flex-wrap">{meta.days.map((d) => (
        <button key={d} onClick={() => setF({ ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d] })} data-testid={`sched-settings-day-${d}`}
          className={`px-3 py-1 rounded-full text-[12px] border ${f.days.includes(d) ? 'bg-[#5E5068] text-white border-[#5E5068]' : 'bg-white border-[#E5E0EA]'}`}>{d}</button>
      ))}<span className="text-[11px] text-[#8A7B95] self-center ml-2">Days shown on the boards</span></div>
    </div>
  );
};

export default function LudoSchedule() {
  const [meta, setMeta] = useState(null);
  const [start, setStart] = useState(iso(new Date()));
  const [wk, setWk] = useState(null);
  const [staff, setStaff] = useState([]);
  const [timeOff, setTimeOff] = useState([]);
  const [clients, setClients] = useState([]);
  const [sessionFor, setSessionFor] = useState(null);
  const [cover, setCover] = useState(null);

  const load = useCallback(async () => {
    try {
      const [w, s, t] = await Promise.all([api.get('/ludo/schedule/week', { params: { start } }), api.get('/ludo/schedule/staff'), api.get('/ludo/schedule/time-off')]);
      setWk(w.data); setStaff(s.data.staff); setTimeOff(t.data.time_off);
    } catch (e) { toast.error(errMsg(e)); }
  }, [start]);
  useEffect(() => {
    api.get('/ludo/schedule/meta').then((r) => setMeta(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get('/ludo/clients').then((r) => setClients((r.data.clients || []).filter((c) => c.stage !== 'Closed'))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!meta || !wk) return <div className="p-8 text-[#685B72]">Loading schedule…</div>;
  const staffName = (id) => staff.find((s) => s.staff_id === id)?.name || 'Unknown';
  const days = wk.settings.days;
  const openBlock = (b) => (b.addFor ? setSessionFor({ client_id: b.addFor, type: b.type }) : setCover(b));
  const s = wk.summary;

  return (
    <div className="space-y-6" data-testid="ludo-schedule-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#87A096] font-semibold">Ludo ABA · Scheduling</div>
          <h1 className="font-serif text-4xl text-[#2D2833]">Weekly Schedule</h1>
          <p className="text-[#685B72] mt-1 text-sm">Repeating weekly sessions per client. Split a day between two BTs, log PTO and call-outs, and fill every gap from one place.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setStart(shift(wk.week_start, -7))} data-testid="sched-prev-week"><ChevronLeft size={14} /></Button>
          <div className="text-sm font-semibold text-[#5E5068] min-w-[150px] text-center" data-testid="sched-week-label">Week of {wk.week_start}</div>
          <Button variant="outline" size="sm" onClick={() => setStart(shift(wk.week_start, 7))} data-testid="sched-next-week"><ChevronRight size={14} /></Button>
          <Button variant="ghost" size="sm" onClick={() => setStart(iso(new Date()))} data-testid="sched-this-week">This week</Button>
          <Button onClick={() => setSessionFor({})} className="rounded-full bg-[#5E5068] text-white" data-testid="sched-add-session"><Plus size={14} className="mr-1" />Add session</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Sessions" value={s.sessions} testid="sched-stat-sessions" />
        <Stat label="Covered" value={s.covered} tone="text-[#3F7D6E]" testid="sched-stat-covered" />
        <Stat label="Gaps to fix" value={s.gaps} tone={s.gaps ? 'text-[#8A3A3A]' : 'text-[#3F7D6E]'} testid="sched-stat-gaps" />
        <Stat label="Covered direct hrs" value={s.direct_hours} testid="sched-stat-hours" />
      </div>
      <Tabs defaultValue="board">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0">
          <TabsTrigger value="board" className={TAB} data-testid="sched-tab-board">Client board</TabsTrigger>
          <TabsTrigger value="staffboard" className={TAB} data-testid="sched-tab-staffboard">Staff board</TabsTrigger>
          <TabsTrigger value="gaps" className={TAB} data-testid="sched-tab-gaps">Gaps · {s.gaps}</TabsTrigger>
          <TabsTrigger value="staff" className={TAB} data-testid="sched-tab-staff">Staff & PTO</TabsTrigger>
          <TabsTrigger value="settings" className={TAB} data-testid="sched-tab-settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="board" className="pt-4"><WeekBoard wk={wk} days={days} staffName={staffName} onBlock={openBlock} onAdd={(cid, wd) => setSessionFor({ client_id: cid, weekday: wd })} /></TabsContent>
        <TabsContent value="staffboard" className="pt-4"><StaffBoard wk={wk} days={days} staffName={staffName} onBlock={openBlock} presets={meta.task_presets} reload={load} /></TabsContent>
        <TabsContent value="gaps" className="pt-4"><GapsPanel gaps={wk.gaps} blocks={wk.blocks} onFix={openBlock} /></TabsContent>
        <TabsContent value="staff" className="pt-4"><StaffManager staff={staff} timeOff={timeOff} meta={meta} reload={load} /></TabsContent>
        <TabsContent value="settings" className="pt-4"><SettingsForm settings={wk.settings} meta={meta} onSaved={load} /></TabsContent>
      </Tabs>
      <SessionDialog key={JSON.stringify(sessionFor)} open={!!sessionFor} initial={sessionFor} meta={meta} clients={clients} staff={staff}
        onClose={() => setSessionFor(null)} onSaved={() => { setSessionFor(null); load(); }} />
      <CoverDialog block={cover} onClose={() => setCover(null)} onDone={() => { setCover(null); load(); }}
        onEdit={(b) => { setCover(null); setSessionFor({ session_id: b.session_id, client_id: b.client_id, weekday: b.weekday, start: b.start, end: b.end, type: b.type, staff_id: b.regular_staff_id || '', effective_from: b.effective_from || '', effective_to: b.effective_to || '', location: b.location || 'Center' }); }} />
    </div>
  );
}
